import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useInternalUserProfilesQuery } from '@/modules/internal-user-queries';
import { MARKETING_CMS_SITES_PATH, marketingCmsSitePath } from '@/views/marketing/marketing-routes';
import { getPersonName } from 'utils';
import { useAuthContext } from 'views/auth/Auth';

import { slugifyAdvisorDisplayName } from './cms-editor-state';
import { useCmsPermissions } from './cms-permissions';
import { useCreateCmsMicrosite } from './cms-queries';
import { CmsFieldErrorList, CmsPageHeader, CmsSectionCard } from './cms-ui';

const getUserDisplayName = (profile: ReturnType<typeof useAuthContext>['user']) => {
    if (!profile) {
        return '';
    }
    const name = getPersonName(profile).trim();
    return name || profile.email || profile.profileGUID || '';
};

export const CreateCmsMicrosite = () => {
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const permissions = useCmsPermissions();
    const { data: internalUsers = [], isLoading } = useInternalUserProfilesQuery({ enabled: permissions.canAdmin });
    const createMutation = useCreateCmsMicrosite();
    const userDisplayName = getUserDisplayName(user);
    const [ownerUserGUID, setOwnerUserGUID] = useState(user?.profileGUID ?? '');
    const [advisorDisplayName, setAdvisorDisplayName] = useState(userDisplayName);
    const [siteName, setSiteName] = useState(userDisplayName ? `${userDisplayName} Microsite` : '');
    const [slug, setSlug] = useState(userDisplayName ? slugifyAdvisorDisplayName(userDisplayName) : '');
    const [errors, setErrors] = useState<string[]>([]);

    const advisorOptions = useMemo(
        () =>
            internalUsers
                .map(({ profileGUID, displayName, email }) => {
                    const label =
                        displayName.toLocaleLowerCase() === email.toLocaleLowerCase()
                            ? displayName
                            : `${displayName} (${email})`;

                    return {
                        value: profileGUID,
                        displayName,
                        label,
                    };
                })
                .sort((left, right) => {
                    const displayNameComparison = left.displayName.localeCompare(right.displayName, undefined, {
                        sensitivity: 'base',
                    });

                    return (
                        displayNameComparison ||
                        left.label.localeCompare(right.label, undefined, { sensitivity: 'base' })
                    );
                }),
        [internalUsers],
    );

    useEffect(() => {
        if (permissions.canAdmin) {
            return;
        }
        setOwnerUserGUID(user?.profileGUID ?? '');
        if (!userDisplayName) {
            return;
        }
        setAdvisorDisplayName((current) => current || userDisplayName);
        setSiteName((current) => current || `${userDisplayName} Microsite`);
        setSlug((current) => current || slugifyAdvisorDisplayName(userDisplayName));
    }, [permissions.canAdmin, user?.profileGUID, userDisplayName]);

    const handleAdvisorChange = (value: string) => {
        const advisor = advisorOptions.find((option) => option.value === value);
        setOwnerUserGUID(value);
        if (advisor) {
            setAdvisorDisplayName(advisor.displayName);
            setSiteName(`${advisor.displayName} Microsite`);
            setSlug(slugifyAdvisorDisplayName(advisor.displayName));
        }
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrors([]);
        if (!ownerUserGUID || !advisorDisplayName || !siteName || !slug) {
            setErrors(['Hub user, site name, display name, and slug are required.']);
            return;
        }

        const cmsMicrositeGUID = await createMutation.mutateAsync({
            ownerUserGUID,
            advisorDisplayName,
            siteName,
            slug,
        });

        if (cmsMicrositeGUID) {
            navigate(marketingCmsSitePath(cmsMicrositeGUID));
        }
    };

    return (
        <>
            <CmsPageHeader
                title="New advisor microsite"
                parent={{ label: 'Advisor Microsites', to: MARKETING_CMS_SITES_PATH }}
            />
            <form className="max-w-3xl space-y-4 p-6" onSubmit={submit}>
                <CmsSectionCard title="Advisor ownership">
                    <CmsFieldErrorList errors={errors} />
                    {permissions.canAdmin && (
                        <div className="space-y-2">
                            <Label>Hub user</Label>
                            <Select value={ownerUserGUID} onValueChange={handleAdvisorChange} disabled={isLoading}>
                                <SelectTrigger>
                                    <SelectValue placeholder={isLoading ? 'Loading Hub users...' : 'Select Hub user'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {advisorOptions.map((advisor) => (
                                        <SelectItem key={advisor.value} value={advisor.value}>
                                            {advisor.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="advisorDisplayName">Advisor display name</Label>
                        <Input
                            id="advisorDisplayName"
                            value={advisorDisplayName}
                            onChange={(event) => {
                                setAdvisorDisplayName(event.target.value);
                                if (!slug) {
                                    setSlug(slugifyAdvisorDisplayName(event.target.value));
                                }
                            }}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="siteName">Site name</Label>
                        <Input id="siteName" value={siteName} onChange={(event) => setSiteName(event.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="slug">Slug</Label>
                        <Input
                            id="slug"
                            value={slug}
                            onChange={(event) => setSlug(slugifyAdvisorDisplayName(event.target.value))}
                        />
                        <p className="text-muted-foreground text-sm">
                            Public path preview: https://claroadvisors.com/advisors/{slug || 'slug'}
                        </p>
                    </div>
                    <Button type="submit" disabled={createMutation.isPending}>
                        Create microsite
                    </Button>
                </CmsSectionCard>
            </form>
        </>
    );
};
